
import Vue from "vue";
import ListTemplate from "@/components/templates/ListTemplate.vue";
import MemoList from "@/components/organisms/MemoList.vue";
import MemoDetail from "@/components/organisms/MemoDetail.vue";
import { NavigationGuardNext } from "vue-router";
import { Dialog } from "@/lib/utils";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/SimpleDialog.vue";
import { Memo } from "@/lib/models/memo";

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },

  components: { ListTemplate, MemoList, MemoDetail },

  data() {
    return {
      isChanged: false,
      isInitialized: false,
    };
  },

  props: {
    novelId: {
      type: String,
    },
    memoId: {
      type: String,
    },
  },

  async created() {
    try {
      await this.$store.dispatch("memoModule/initialize");
      this.isInitialized = true;
    } catch (error) {
      console.error("memoModule/initialize error:", error);
      this.isInitialized = true;
    }
    window.addEventListener("beforeunload", this.beforeUnload);
  },

  mounted() {
    this.waitForInitializationAndSetup();
  },

  destroyed() {
    window.removeEventListener("beforeunload", this.beforeUnload);
  },

  computed: {
    recentMemoId() {
      return this.$store.getters["memoModule/recentMemoId"];
    },
  },

  methods: {
    waitForInitializationAndSetup() {
      const checkInitialization = () => {
        if (this.isInitialized) {
          this.setupMemoNavigation();
        } else {
          setTimeout(checkInitialization, 50);
        }
      };
      checkInitialization();
    },
    setupMemoNavigation() {
      const { $refs, $route, $router, novelId, recentMemoId } = this;

      this.$nextTick(() => {
        const { memoList } = $refs as any;
        if (!memoList) return;

        const filteredMemos: Memo[] = this.$store.getters["memoModule/memosFromMemoTagsAndNovel"](novelId, []);

        if ($route.params.memoId === recentMemoId) return;

        if (filteredMemos.length === 0) {
          memoList.onCreateMemoButtonClick();
        } else {
          let memoId;
          if (filteredMemos.some((memo) => memo.id === recentMemoId)) memoId = recentMemoId;
          else memoId = filteredMemos[0].id;

          if ($route.params.novelId === novelId && $route.params.memoId === memoId) {
            return;
          }

          $router.push({
            name: $route.name as string,
            params: { novelId, memoId },
          });
        }
      });
    },
    onUpdatedIsChanged(isChanged: boolean) {
      this.isChanged = isChanged;
    },
    showLeaveConfirmDialog(next) {
      const confirmDialog = new Dialog(SimpleDialog);
      const options: SimpleDialogProps = {
        title: "開いているメモを編集中です。",
        content: "保存していないデータが存在します。\nこのまま画面遷移しますか？\n※編集前のデータに戻ります。",
        negative: "キャンセル",
        positive: "遷移する",
        positiveCallback: () => {
          // このタイミングでnext()を実行すると
          // なぜかもう一度beforeRouteLeaveが走るので、isChangedをfalseにしてダイアログを出さずに画面遷移をするようにする。
          this.isChanged = false;
          next();
        },
      };

      confirmDialog.show(options);
    },
    beforeUnload(event): void {
      if (this.isChanged) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = "開いているメモを編集中です。";
      }
    },
  },

  beforeRouteUpdate(to, from, next) {
    if (this.isChanged) {
      this.showLeaveConfirmDialog(next);
      return;
    }
    next();
  },
  beforeRouteLeave(to, from, next) {
    if (this.isChanged) {
      this.showLeaveConfirmDialog(next);
      return;
    }
    next();
  },
});

interface Data {
  isChanged: boolean;
  isInitialized: boolean;
}

interface Props {
  novelId: string;
  memoId: string;
}

interface Computed {
  recentMemoId: string;
}

interface Methods {
  waitForInitializationAndSetup: () => void;
  setupMemoNavigation: () => void;
  showLeaveConfirmDialog: (next: NavigationGuardNext<Vue>) => void;
  beforeUnload: (event: BeforeUnloadEvent) => void;
  onUpdatedIsChanged: (isChanged: boolean) => void;
}
